import svgQuote from '@img/icons/background/quote.svg?url';
import svgBullet from '@img/icons/bullet-check-circle-magenta.svg?url';
import svgBulletYellow from '@img/icons/bullet-check-circle-yellow.svg?url';
import { css } from 'styled-components';
import { transition } from './animate';
import brand from './brand';
import { fluid } from './fluid';
import fonts from './fonts';
import scrollbars from './scrollbars';
export const headingStyles = css `
  ${fonts.ubuntu.bold};

  color: currentColor;
  line-height: 1.2em;
  margin: 0 0 ${fluid(24, 32)};
`;
export const h1 = css `
  ${headingStyles};

  font-size: ${fluid(30, 60)};
`;
export const h2 = css `
  ${headingStyles};

  font-size: ${fluid(26, 46)};
`;
export const h3 = css `
  ${headingStyles};

  font-size: ${fluid(20, 32)};
`;
export const h4 = css `
  ${headingStyles};

  font-size: ${fluid(18, 24)};
`;
export const paragraphStyles = css `
  ${fonts.openSans.light};

  color: currentColor;
  line-height: 1.5em;
  margin: 0 0 ${fluid(16, 24)};
`;
export const paragraphLarge = css `
  ${paragraphStyles};

  font-size: ${fluid(18, 20)};
`;
export const paragraphRegular = css `
  ${paragraphStyles};

  font-size: ${fluid(16, 18)};
`;
export const paragraphSmall = css `
  ${paragraphStyles};

  font-size: ${fluid(12, 14)};
`;
export const link = css `
  ${transition('text-decoration-color')};

  display: inline-block;
  color: ${brand.primary.purple.base};
  text-decoration: underline;
  text-decoration-color: currentColor;
  text-decoration-thickness: 1px;
  text-underline-offset: 3px;

  &:focus {
    outline: none;
  }

  &:focus-visible {
    outline: auto;
    outline-offset: 2px;
    outline-width: 1px;
    outline-style: solid;
  }

  &:focus,
  &:hover {
    text-decoration-color: transparent;
  }
`;
export const blockquote = css `
  ${paragraphLarge};

  color: currentColor;
  text-align: left;
  position: relative;
  padding-left: ${fluid(58, 80)};
  background-size: ${fluid(42, 56)};
  background-repeat: no-repeat;
  background-position: top left;
  background-image: url(${svgQuote});
`;
export const table = css `
  ${paragraphRegular};

  background: ${brand.global.white};
  border-collapse: collapse;
  color: currentColor;
  margin-bottom: 1em;
  width: 100% !important;

  td,
  th {
    border-right: 2px solid ${brand.global.white};
    padding: 12px 16px;
    text-align: left;
  }

  thead {
    tr {
      background: ${brand.primary.purple.base};

      td,
      th {
        ${fonts.ubuntu.bold};

        color: ${brand.global.white};
      }
    }
  }

  tbody {
    tr {
      &:nth-child(odd) {
        background-color: ${brand.grey.grey97};
      }

      &:nth-child(even) {
        background-color: ${brand.grey.grey90};
      }
    }
  }
`;
export const contentStyles = css `
  --headingColor: ${brand.primary.purple.base};
  --linkColor: ${brand.primary.purple.base};
  --contentColor: currentColor;
  --bulletTickColor: ${brand.secondary.pink.light};

  color: var(--contentColor);

  &.secondary-4,
  &.tertiary-4 {
    --contentColor: ${brand.global.black};
    --headingColor: ${brand.global.black};
    --linkColor: ${brand.global.black};
  }

  .primary &,
  .secondary &,
  .tertiary &,
  .secondary-2 &,
  .secondary-3 &,
  .tertiary-2 &,
  .tertiary-3 &,
  .tertiary-5 &,
  .tertiary-6 & {
    --headingColor: ${brand.global.white};
    --linkColor: ${brand.global.white};
    --contentColor: ${brand.global.white};
    --bulletTickColor: ${brand.tertiary.yellow.light};
  }

  h1,
  .h1-styles {
    ${h1};
  }

  h2,
  .h2-styles {
    ${h2};
  }

  h3,
  .h3-styles {
    ${h3};
  }

  h4,
  .h4-styles {
    ${h4}
  }

  h5,
  .h5-styles {
    ${paragraphLarge}
    ${fonts.ubuntu.bold};
  }

  h1,
  .h1-styles,
  h2,
  .h2-styles,
  h3,
  .h3-styles,
  h4,
  .h4-styles,
  h5,
  .h5-styles {
    color: var(--headingColor);
    margin: ${fluid(24, 32)} 0 ${fluid(16, 24)};
  }

  p {
    ${paragraphRegular}

    a {
      ${link};

      color: var(--linkColor);
    }
  }

  strong {
    ${fonts.openSans.extraBold};
  }

  .large {
    ${paragraphLarge}
  }

  .small {
    ${paragraphSmall}
  }

  a {
    ${link}

    color: var(--linkColor);
  }

  ol,
  ul {
    ${paragraphRegular};

    margin: ${fluid(8, 12)} 0 ${fluid(24, 32)};

    li {
      color: currentColor;
      margin: 0 0 ${fluid(8, 12)};
    }
  }

  ol,
  ul {
    list-style: none;
    padding-left: 1.2em;

    li::before {
      color: currentColor;
      display: inline-block;
      width: 1.2em;
      margin-left: -1.2em;
    }
  }

  ol {
    counter-reset: li;

    li {
      counter-increment: li;

      &::before {
        content: counter(li) '.';
        width: 2.5ch; // Allow space for double digit numbers
      }
    }
  }

  .bullet-point {
    display: block;
    position: relative;
    padding-left: 39px;

    &::before {
      content: '';      
      width: 24px;
      height: 24px;
      position: absolute;
      top: 2px;
      left: 0;
      background-size: ${fluid(24, 24)};
      background-repeat: no-repeat;
      background-position: top left;
      background-image: url(${svgBullet});

      .primary &,
      .secondary &,
      .tertiary &,
      .secondary-2 &,
      .secondary-3 &,
      .tertiary-2 &,
      .tertiary-3 &,
      .tertiary-5 &,
      .tertiary-6 & {
        background-image: url(${svgBulletYellow});
      }
    }
  }

  // Ensure that inline styling hasn't been applied
  ul:not([style*='list-style-type']) li::before {
    content: '\\2022';
    font-weight: bold;
  }

  ul:has(.bullet-point) {
    padding: 0;

    li::before {
      display: none;
    }
  }

  blockquote {
    ${blockquote};
  }

  hr {
    display: block;
    background: ${brand.grey.grey40};
    border: 0;
    height: 2px;
    margin: ${fluid(24, 32)} 0;
    padding: 0;
    width: 100%;
  }

  iframe {
    margin-bottom: 1em;
  }

  table {
    ${table};
  }

  .responsive-table {
    ${scrollbars};

    position: relative;
    overflow-x: auto;
  }

  > *:first-child {
    margin-top: 0;
  }

  > *:last-child {
    margin-bottom: 0;
  }
`;
